import { useState } from "react";
import { RefreshCw, UserPlus } from "lucide-react";
import { RetroWindow } from "./RetroWindow";
import { RetroSuccess } from "./RetroSuccess";
import { FlowerLogo } from "../FlowerLogo";
import { useRegisterForm } from "../RegisterForm";
import { useI18n } from "../../lib/i18n";

const F = ({ id, label, ...p }) => (
  <label htmlFor={id} className="grid sm:grid-cols-[150px_1fr] items-center gap-1 sm:gap-3">
    <span>{label}</span>
    <input id={id} data-testid={`register-${id}-input`} className="w98-field" {...p} />
  </label>
);

const Form = ({ onSuccess }) => {
  const { t } = useI18n();
  const { form, set, error, loading, captcha, reload, submit } = useRegisterForm(onSuccess);
  return (
    <form onSubmit={submit} data-testid="register-form" className="space-y-3">
      <p className="text-sm">{t("regSub")}</p>
      <F id="password" label={t("fPassword")} type="password" value={form.password} onChange={set("password")} autoComplete="new-password" />
      <F id="confirm" label={t("fConfirm")} type="password" value={form.confirm} onChange={set("confirm")} autoComplete="new-password" />
      <F id="nickname" label={t("fNick")} value={form.nickname} onChange={set("nickname")} placeholder={t("optional")} />
      <F id="email" label={t("fEmail")} type="email" value={form.email} onChange={set("email")} placeholder={t("optional")} />
      <p className="text-xs text-[#404040]">{t("pwHint")}</p>
      <fieldset>
        <legend>{t("rtSecurity")}</legend>
        <p className="text-sm mb-2">{t("fCaptcha")}</p>
        <div className="flex flex-wrap items-center gap-2">
          <div className="w98-sunken bg-white h-[70px] w-[200px] p-[2px] flex items-center justify-center">
            {captcha?.image ? <img src={captcha.image} alt="captcha" data-testid="captcha-image" className="h-full w-full" />
              : <span className="text-xs">{captcha?.error ? t("captchaFail") : "…"}</span>}
          </div>
          <button type="button" onClick={reload} className="w98-btn !min-w-0 !px-2" data-testid="captcha-refresh-btn" aria-label={t("captchaRefresh")}>
            <RefreshCw className="h-4 w-4" />
          </button>
          <input data-testid="register-captcha-input" value={form.captcha} onChange={set("captcha")} maxLength={5} autoComplete="off"
            placeholder={t("captchaPh")} className="w98-field !w-auto flex-1 min-w-[120px] uppercase tracking-[0.3em]" />
        </div>
      </fieldset>
      {error && (
        <div data-testid="register-error" className="flex items-start gap-2 w98-sunken bg-white p-2 text-sm">
          <span className="h-5 w-5 shrink-0 rounded-full bg-[#ff0000] text-white text-xs font-bold flex items-center justify-center">×</span>
          <span><b>{t("rtError")}:</b> {error}</span>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-1 border-t border-[#808080] shadow-[0_1px_0_#fff_inset]">
        <button type="submit" disabled={loading} className="w98-btn primary mt-3" data-testid="register-submit-btn">
          {loading ? t("submitting") : t("rtNext")}
        </button>
      </div>
    </form>
  );
};

export const RetroRegister = ({ onRegistered, className }) => {
  const { t } = useI18n();
  const [result, setResult] = useState(null);
  return (
    <RetroWindow id="register" testid="retro-register-window" className={className} title={t("rtWizard")} icon={<UserPlus className="h-4 w-4" />}>
      <div className="flex gap-4">
        <div className="hidden md:flex w-32 shrink-0 bg-gradient-to-b from-[#000080] to-[#1084d0] items-start justify-center pt-6 w98-soft">
          <FlowerLogo size={84} />
        </div>
        <div className="flex-1 min-w-0">
          {result ? <RetroSuccess result={result} onReset={() => setResult(null)} />
            : <Form onSuccess={(r) => { setResult(r); onRegistered?.(); }} />}
        </div>
      </div>
    </RetroWindow>
  );
};
